import React, { useState } from 'react';
import { StackShim as Stack } from '@fluentui/react-migration-v8-v9';
import { Field, Input, FluentProvider, webLightTheme } from '@fluentui/react-components';
import { Controller, FieldValues, UseFormMethods } from 'react-hook-form';
import CompanyCodeInput from '../../../companyCreation/CompanyCodeInput';
import { ICompanyNameSapInfo } from '../../../../utils/types/ICompanyNameSapInfo';
import CountryPicker from '../../../countries/CountryPicker';
import ICountry from '../../../../utils/types/ICountry';
import StateProvinceRegionDropDown from '../../../companyCreation/details/StateProvinceRegionDropDown';
import IDropdownOption from '../../../../utils/types/IDropdownOption';
import ITag from '../../../../utils/types/ITag';

interface ICompanyNameSapInfoFormProps {
  form: UseFormMethods<FieldValues>;
  data?: ICompanyNameSapInfo;
}

const CompanyNameSapInfoForm = ({ form, data }: ICompanyNameSapInfoFormProps): JSX.Element => {
  const initialSelectedCountry = data?.physicalCountry
    ? [
        {
          key: data?.physicalCountry?.id,
          text: data?.physicalCountry?.shortName,
          name: data?.physicalCountry?.shortName,
        },
      ]
    : null;
  const [selectedCountry, setSelectedCountry] = useState<ITag[]>(initialSelectedCountry);
  const { control, errors, setValue, formState, trigger } = form;

  const onCountryUpdate = (items: ITag[]): void => {
    const countries = items.map((item) => {
      const newObject: ICountry = {
        id: item.key.toString(),
        shortName: item.name,
      };
      return newObject;
    });
    setSelectedCountry(items);
    setValue('physicalCountry', countries.length ? countries[0] : null);
    setValue('sapRegion', null);
    if (formState.isSubmitted) {
      trigger();
    }
  };

  const onSapRegionUpdate = (
    event: React.ChangeEvent<HTMLSelectElement>,
    option?: IDropdownOption,
    index?: number,
  ): void => {
    setValue('sapRegion', { id: option?.key, name: option?.text });
  };

  return (
    <FluentProvider theme={webLightTheme}>
      <Stack tokens={{ childrenGap: 10 }}>
        <CompanyCodeInput
          defaultValue={data?.companyCode}
          readOnly
          form={form}
          fieldName="companyCode"
          label="Company Code"
        />
        <Controller
          name="legalFullName"
          control={control}
          defaultValue={data?.legalFullName || ''}
          render={({ onChange, value, name }): React.ReactElement => (
            <Field
              label="Legal Full Name"
              required
              validationMessage={errors?.legalFullName?.message}
            >
              <Input
                id="legalFullName"
                name={name}
                value={value}
                onChange={(e, inputData): void => {
                  onChange(inputData.value);
                  if (formState.isSubmitted) {
                    trigger('legalFullName');
                  }
                }}
              />
            </Field>
          )}
          rules={{
            maxLength: {
              value: 200,
              message: 'Legal Full Name cannot have more than 200 characters',
            },
            required: {
              value: true,
              message: 'Legal Full Name is required.',
            },
          }}
        />
        <Controller
          name="sapLegalName"
          control={control}
          defaultValue={data?.sapLegalName || ''}
          render={({ onChange, value, name }): React.ReactElement => (
            <Field
              label="SAP Legal Name"
              required
              validationMessage={errors?.sapLegalName?.message}
            >
              <Input
                id="sapLegalName"
                name={name}
                value={value}
                onChange={(e, inputData): void => {
                  onChange(inputData.value);
                }}
              />
            </Field>
          )}
          rules={{
            maxLength: {
              value: 200,
              message: 'SAP Legal Name cannot have more than 200 characters',
            },
            required: {
              value: true,
              message: 'SAP Legal Name is required.',
            },
          }}
        />
        <Controller
          name="sapNameShortForm"
          control={control}
          defaultValue={data?.sapNameShortForm || ''}
          render={({ onChange, value, name }): React.ReactElement => (
            <Field
              label="SAP Name (Short Form)"
              required
              validationMessage={errors?.sapNameShortForm?.message}
            >
              <Input
                id="sapNameShortForm"
                name={name}
                value={value}
                onChange={(e, inputData): void => {
                  onChange(inputData.value);
                }}
              />
            </Field>
          )}
          rules={{
            maxLength: {
              value: 30,
              message: 'SAP Name (Short Form) cannot have more than 30 characters',
            },
            required: {
              value: true,
              message: 'SAP Name (Short Form) is required.',
            },
          }}
        />
        <Controller
          name="physicalCountry"
          control={control}
          defaultValue={initialSelectedCountry}
          required
          rules={{
            required: {
              value: true,
              message: 'Please enter a country',
            },
          }}
          render={(): React.ReactElement => (
            <CountryPicker
              itemLimit={1}
              label="Physical Country"
              selectedItems={selectedCountry}
              setSelectedItems={onCountryUpdate}
              isDisabled={false}
              errorMessage={errors?.physicalCountry?.message}
            />
          )}
        />
        <Controller
          name="sapRegion"
          control={control}
          defaultValue={data?.sapRegion}
          render={(): React.ReactElement => (
            <StateProvinceRegionDropDown
              selectedCountry={selectedCountry}
              defaultValue={data?.sapRegion?.id}
              onChange={onSapRegionUpdate}
              label="SAP Region"
              disabled={false}
            />
          )}
        />
      </Stack>
    </FluentProvider>
  );
};

export default CompanyNameSapInfoForm;
